/**
 * WipFilters
 *
 * Header controls row: hierarchical Branch filter, Cut-Off Period selector,
 * multi-select Staff dropdown (search + checkboxes), Client/Engagement
 * selector, and a CSV export button (client-side -- no backend export
 * endpoint exists, since no single backend owns the joined SSS+TRS data).
 *
 * NOTE: this uses lightweight self-contained dropdown markup so the file is
 * drop-in runnable on its own. If wiring into the existing Staff Audit
 * Scheduler frontend, swap the staff multi-select and engagement select for
 * the project's existing shared `MultiSelectDropdown` / `SearchableSelectField`
 * components instead of duplicating dropdown logic.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, Download, Search } from 'lucide-react';
import { useBranchContext } from '../../hooks/useBranchContext.js';

/** @typedef {{ id: string, name: string }} Option */

/**
 * @param {{
 *   staffOptions: Option[],
 *   selectedStaffIds: string[],
 *   onChangeStaffIds: (ids: string[]) => void,
 *   engagementOptions: Option[],
 *   selectedEngagementId: string|null,
 *   onChangeEngagementId: (id: string|null) => void,
 *   onExport: () => void,
 * }} props
 */
export const WipFilters = ({
  staffOptions,
  selectedStaffIds,
  onChangeStaffIds,
  engagementOptions,
  selectedEngagementId,
  onChangeEngagementId,
  onExport,
}) => {
  const { branches, activeBranchId, setActiveBranchId, activeCutOff, setActiveCutOff } =
    useBranchContext();
  const [staffMenuOpen, setStaffMenuOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');

  const filteredStaffOptions = useMemo(
    () =>
      staffOptions.filter((s) => s.name.toLowerCase().includes(staffSearch.trim().toLowerCase())),
    [staffOptions, staffSearch]
  );

  const toggleStaff = (id) => {
    onChangeStaffIds(
      selectedStaffIds.includes(id)
        ? selectedStaffIds.filter((s) => s !== id)
        : [...selectedStaffIds, id]
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-edge bg-surface p-3">
      {/* Branch filter */}
      <select
        value={activeBranchId ?? ''}
        onChange={(e) => setActiveBranchId(e.target.value || null)}
        className="rounded-md border border-edge bg-base px-3 py-2 text-sm text-primary focus:border-accent focus:outline-none"
      >
        <option value="">All Branches</option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.parentId ? `\u2014 ${b.name}` : b.name}
          </option>
        ))}
      </select>

      {/* Cut-off selector */}
      <div className="flex overflow-hidden rounded-md border border-edge">
        {[
          { value: 'cutoff_1', label: 'Cut-Off 1 (1st\u201315th)' },
          { value: 'cutoff_2', label: 'Cut-Off 2 (16th\u2013End)' },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setActiveCutOff(opt.value)}
            className={`px-3 py-2 text-sm transition-colors ${
              activeCutOff === opt.value
                ? 'bg-accent text-white'
                : 'bg-base text-secondary hover:bg-surface-hover'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Staff multi-select */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setStaffMenuOpen((v) => !v)}
          className="flex items-center gap-2 rounded-md border border-edge bg-base px-3 py-2 text-sm text-primary hover:border-edge"
        >
          {selectedStaffIds.length > 0 ? `${selectedStaffIds.length} staff selected` : 'All Staff'}
          <ChevronDown className="h-4 w-4 text-muted" />
        </button>
        {staffMenuOpen && (
          <div className="absolute z-20 mt-1 w-64 rounded-md border border-edge bg-base shadow-lg">
            <div className="flex items-center gap-2 border-b border-edge px-3 py-2">
              <Search className="h-4 w-4 text-muted" />
              <input
                value={staffSearch}
                onChange={(e) => setStaffSearch(e.target.value)}
                placeholder="Search staff..."
                className="w-full bg-transparent text-sm text-primary focus:outline-none"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filteredStaffOptions.map((s) => (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-surface"
                >
                  <input
                    type="checkbox"
                    checked={selectedStaffIds.includes(s.id)}
                    onChange={() => toggleStaff(s.id)}
                    className="h-3.5 w-3.5 rounded border-edge bg-surface-hover text-accent"
                  />
                  {s.name}
                </label>
              ))}
              {filteredStaffOptions.length === 0 && (
                <p className="px-3 py-2 text-sm text-muted">No staff match.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Engagement selector */}
      <select
        value={selectedEngagementId ?? ''}
        onChange={(e) => onChangeEngagementId(e.target.value || null)}
        className="rounded-md border border-edge bg-base px-3 py-2 text-sm text-primary focus:border-accent focus:outline-none"
      >
        <option value="">All Clients / Engagements</option>
        {engagementOptions.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name}
          </option>
        ))}
      </select>

      {/* Export */}
      <div className="ml-auto">
        <button
          type="button"
          onClick={onExport}
          className="flex items-center gap-1.5 rounded-md border border-edge px-3 py-2 text-sm text-primary hover:bg-surface-hover"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
    </div>
  );
};
